import type { RefObject } from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import type { DeepPartial } from 'react-hook-form';
import { OpenTelemetryIcon } from '@gonfalon/icons/OpenTelemetryIcon';
import { Message } from '@gonfalon/messages/Message';
import { msg } from '@gonfalon/messages/msg';
import { skeletonStyles } from '@gonfalon/metric-events/Sources';
import { toDataSourcesMetricDetails } from '@gonfalon/navigator/navigators';
import { getIntegrationManifestQuery } from '@gonfalon/rest-api/queries/integrationManifests';
import { LinkButton, Tooltip, TooltipTrigger } from '@launchpad-ui/components';
import { Icon } from '@launchpad-ui/icons';
import { useQuery } from '@tanstack/react-query';
import clsx from 'clsx';

import { trackMetricsEvent } from '../../analytics';
import { isLDHosted } from '../../isLDHosted';
import type { MetricDataSource } from '../../types';

import styles from './MetricDataSourceBadge.module.css';

function useIsTruncated(ref: RefObject<HTMLElement | null>, deps: unknown[] = []) {
  // Determine whether the text content of the referenced element is truncated via CSS
  const [isTruncated, setIsTruncated] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) {
      return;
    }

    const check = () => setIsTruncated(el.scrollWidth > el.clientWidth);
    check();

    const observer = new ResizeObserver(check);
    observer.observe(el);
    return () => observer.disconnect();
  }, deps);

  return isTruncated;
}

type ElementProps = Pick<React.HTMLAttributes<HTMLDivElement>, 'className' | 'style'>;

const BadgeContainer: React.FC<
  {
    children: React.ReactNode;
    dataSourceDetailPath?: ReturnType<typeof toDataSourcesMetricDetails>;
    size?: 'small' | 'normal';
    color?: 'variant1' | 'default';
  } & ElementProps
> = ({ children, size, color, dataSourceDetailPath, ...elementProps }) => {
  const variableClasses = useMemo(
    () => ({
      [styles.small]: size === 'small',
      [styles.colorVariant1]: color === 'variant1',
    }),
    [size, color],
  );

  if (dataSourceDetailPath) {
    return (
      <LinkButton
        href={dataSourceDetailPath}
        style={elementProps.style}
        className={clsx(elementProps.className, styles.metricDataSourceBadge, variableClasses, styles.isLink)}
        onClick={() => trackMetricsEvent('Clicked Metric Data Source Badge Link')}
      >
        {children}
      </LinkButton>
    );
  } else {
    return (
      <div
        style={elementProps.style}
        className={clsx(elementProps.className, styles.metricDataSourceBadge, variableClasses)}
      >
        {children}
      </div>
    );
  }
};

type MinimalMetricDataSource = MetricDataSource | (DeepPartial<MetricDataSource> & Pick<MetricDataSource, 'key'>);

export const MetricDataSourceBadge = ({
  metricDataSource,
  displaySourceLabel,
  displayWarehouseName,
  size,
  color,
  ...elementProps
}: {
  metricDataSource: MinimalMetricDataSource;
  size?: 'small' | 'normal';
  color?: 'default' | 'variant1';
  displaySourceLabel?: boolean;
  displayWarehouseName?: boolean;
} & ElementProps) => {
  const {
    // Project key is required for generating the url to the data source details page
    _projectSummary,
    // Integration key is required for fetching the integration manifest icon
    integrationKey,
    // Key is required to determining whether the data source is internal or external, and
    // for generating the url to the data source details page
    key,
    // Name is Required for displaying the source name if not interally hosted or using displayWarehouseName
    name,
  } = metricDataSource ?? {};
  const isLD = isLDHosted(key);
  // For external warehouse sources: fetch the integration manifest to get the icon
  const { data: integrationManifest, isLoading } = useQuery({
    ...getIntegrationManifestQuery({ integrationKey: integrationKey as string }),
    // Only attempt fetching the icon if an integrationKey is provided and the source is not LaunchDarkly
    enabled: !isLD && Boolean(integrationKey),
  });
  const nameRef = useRef<HTMLElement>(null);
  const optionalSourceLabel = useMemo(() => {
    return displaySourceLabel ? (
      <span className={styles.sourceLabel}>
        <Message id="metrics.dataSourceBadge.source" />{' '}
      </span>
    ) : null;
  }, [displaySourceLabel]);
  const sourceIcon = useMemo(() => {
    if (isLD) {
      return <Icon className={styles.warehouseIcon} name="osmo" />;
    } else if (isLoading) {
      return <span className={clsx(styles.warehouseIcon, skeletonStyles.skeleton)} />;
    } else if (integrationManifest?.icons?.square) {
      return (
        <img
          className={styles.warehouseIcon}
          src={integrationManifest?.icons?.square}
          alt={msg('metrics.dataSourceBadge.integrationLogoAlt')}
        />
      );
    } else if (key === 'otel-import') {
      return <OpenTelemetryIcon className={styles.warehouseIcon} />;
    }
  }, [integrationManifest, isLD, isLoading, key]);
  const sourceName = useMemo(() => {
    if (displayWarehouseName) {
      // Note: preloading of integration manifests should ensure that the name is available immediately in this case
      return isLoading ? '' : (integrationManifest?.name ?? '');
    } else if (isLD) {
      return 'LaunchDarkly';
    } else {
      return name || msg('metrics.dataSourceBadge.sourceNotFound');
    }
  }, [integrationManifest, displayWarehouseName, isLD, isLoading, name]);
  // Only show the full name in a tooltip if it is truncated
  const sourceNameIsTruncated = useIsTruncated(nameRef, [sourceName]);
  const dataSourceDetailPath = useMemo(() => {
    // Only provide a link URL for non-LaunchDarkly sources with with a an associated project key
    if (!isLD && typeof key === 'string' && typeof _projectSummary?.key === 'string') {
      return toDataSourcesMetricDetails({ dataSourceKey: key, projectKey: _projectSummary.key });
    }
  }, [key, _projectSummary, isLD]);

  return (
    <TooltipTrigger isDisabled={!sourceNameIsTruncated}>
      <BadgeContainer dataSourceDetailPath={dataSourceDetailPath} size={size} color={color} {...elementProps}>
        {optionalSourceLabel}
        {sourceIcon}
        <span ref={nameRef} className={styles.sourceName}>
          {sourceName}
        </span>
      </BadgeContainer>
      <Tooltip>{sourceName}</Tooltip>
    </TooltipTrigger>
  );
};
